// External imports
import React, { Fragment, useEffect } from 'react';
import {
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableCell,
  Tooltip,
  Button,
  Chip,
  Menu,
  MenuItem,
  withStyles,
} from '@material-ui/core';
import moment from 'moment-timezone';
import { get } from 'lodash';
// Internal imports
import { useSetState } from '~/app/Utility/customHooks';
import {
  checkDBSignaturesExist,
  getDocumentList,
  findValueInObject,
  signatureStatus,
  doWeHaveSignatures,
  SIGNATURE_DOCUMENT_DESC,
} from './signatureListHelper.js';
import { remainingSignaturesNeeded } from '~/app/Pages/Returns/pages/Signatures/signaturesHelper.js';
import {
  SIGNATURE_LIST_TABLE_COLUMNS,
  SIGNEE,
  BANK_APP_INFO_MSG,
  FORM_4547_INFO_MSG,
} from '~/app/constants.js';
import XlinkAPI from '~/app/api/xlinkAPI';
import ErrorHelpers from '~/app/errorHelpers.js';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { doesValueExistInAnArrayOfObjects } from '~/app/Utility/general.js';
import Spinner from '~/app/Components/Common/Spinner.jsx';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import menuVertical from '~/images/icons/menu_vertical.png';
// Redux imports
import { useSelector, useDispatch } from 'react-redux';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer.js';
// Styling imports
import { styles } from '~/app/Pages/Returns/pages/Signatures/components/css/signatureList.js';
import '~/app/Pages/Returns/pages/Signatures/components/css/signatureList.css';

/**
 * Component that displays the status of existing signature requests.
 *
 * @component
 * @category Returns
 * @subcategory Signatures
 */
const SignatureList = props => {
  const { classes } = props;
  const { AMENDED_RETURN, FINAL_RETURN, FNBA } = SIGNATURE_DOCUMENT_DESC;
  const dispatch = useDispatch();

  const {
    signaturesNeeded,
    preparerID,
    amendedPreparerID,
    returnID,
    remoteSignSuccessful,
    inOfficeSignSuccessful,
    isSelfPreparing,
    formList,
  } = useSelector(state => ({
    signaturesNeeded: state.remoteSign.signaturesNeeded,
    preparerID: state.formViewer.preparerID,
    amendedPreparerID: state.formViewer.amendedPreparerID,
    returnID: state.returnProfile.returnID,
    remoteSignSuccessful: state.formViewer.remoteSignSuccessful,
    inOfficeSignSuccessful: state.formViewer.inOfficeSignSuccessful,
    isSelfPreparing: state.formViewer.isSelfPreparing,
    formList: state.returnProfile.formList,
  }));

  const [state, setState] = useSetState({
    taxReturnTime: '',
    bankTime: '',
    fnbaTime: '',
    taxReturnStatus: {},
    bankStatus: {},
    fnbaStatus: {},
    amendedReturnStatus: {},
    signatureDocMap: new Map(),
    docsNeedingSigned: [],
    signaturesReq: [],
    bankID: 0,
    printFinalBankDoc: false,
    isAmendedReturnAttached: false,
    is4547FormAttached: false,
    isResending: false,
    isDeleting: false,
    contextMenuAnchor: null,
    contextMenuSelected: null,
    isConfirmDeleteModalOpen: false,
    deleteArchiveDocID: null,
    otherDocStatuses: [],
    taxReturnDisplayName: FINAL_RETURN,
    fnbaDisplayName: FNBA,
    amendedReturnDisplayName: AMENDED_RETURN,
  });

  useEffect(() => {
    const PFBA = props.printPrefs?.PFBA?.configValue === '1';
    const PFBB = props.printPrefs?.PFBB?.configValue === '1';

    if (PFBA || PFBB) {
      setState({ printFinalBankDoc: true });
    }
  }, [props.printPrefs]);

  useEffect(() => {
    handleCheckDBSignaturesExist();
    handleTimeAndStatus();
  }, [
    remoteSignSuccessful,
    inOfficeSignSuccessful,
    preparerID,
    amendedPreparerID,
    signaturesNeeded,
  ]);

  useEffect(() => {
    if (props.bankID) {
      setState({ bankID: props.bankID });
    }
  }, [props.bankID]);

  useEffect(() => {
    documentsNeedingSigned();
  }, [
    state.bankStatus,
    state.fnbaStatus,
    state.taxReturnStatus,
    state.amendedReturnStatus,
    state.otherDocStatuses,
  ]);

  useEffect(() => {
    const federalFormList = formList.find(list => list?.desc?.trim() === 'FEDERAL');
    const isAmendedReturnAttached = doesValueExistInAnArrayOfObjects(
      federalFormList?.Children || [],
      'USEX',
    );

    const is4547FormAttached = doesValueExistInAnArrayOfObjects(
      federalFormList?.Children || [],
      'US6C01',
    );

    setState({
      isAmendedReturnAttached,
      is4547FormAttached,
    });
  }, [formList]);

  /**
   * Fetches the time and the status from the DB of whether a document has been signed.
   */
  const handleTimeAndStatus = async () => {
    const documentList = await getDocumentList(returnID);
    if (Array.isArray(documentList)) {
      const taxReturn = findValueInObject(documentList, '0000');
      const fnba = findValueInObject(documentList, 'FNBA');
      const amendedReturn = findValueInObject(documentList, 'AMENDED RETURN');
      const signdocMap = new Map();

      if (taxReturn) {
        signdocMap.set(FINAL_RETURN, taxReturn?.documentID);

        setState({
          taxReturnStatus: await signatureStatus(
            taxReturn.documentDescription,
            taxReturn.documentID,
          ),
          taxReturnTime: moment.utc(taxReturn.createDate).local().format('MM/DD/YYYY - hh:mm A'),
          taxReturnDisplayName: taxReturn.documentDisplayName || FINAL_RETURN,
        });
      }

      if (fnba) {
        signdocMap.set(FNBA, fnba?.documentID);

        setState({
          fnbaStatus: await signatureStatus(fnba.documentDescription, fnba.documentID),
          fnbaTime: moment.utc(fnba.createDate).local().format('MM/DD/YYYY - hh:mm A'),
          fnbaDisplayName: fnba.documentDisplayName || FNBA,
        });
      }

      if (amendedReturn) {
        signdocMap.set(AMENDED_RETURN, amendedReturn?.documentID);

        setState({
          amendedReturnStatus: await signatureStatus(
            amendedReturn.documentDescription,
            amendedReturn.documentID,
          ),
          amendedReturnTime: moment
            .utc(amendedReturn.createDate)
            .local()
            .format('MM/DD/YYYY - hh:mm A'),
          amendedReturnDisplayName: amendedReturn.documentDisplayName || AMENDED_RETURN,
        });
      }

      // Collect any other remote signature documents not covered by the known types above.
      // Desktop archives and tracks signatures for any form type; the CLO list should match.
      // Exclude by documentID (not name) — documentName values vary (e.g. "PRINTFINALFORMCALC"
      // vs "0000") so name-based exclusion produces duplicates.
      const handledDocIDs = new Set(
        [taxReturn, fnba, amendedReturn].filter(Boolean).map(d => d.documentID),
      );
      const otherDocs = documentList.filter(
        data =>
          !handledDocIDs.has(data.documentID) && data.documentDescription === 'Remote Signature ',
      );

      const otherDocStatuses = await Promise.all(
        otherDocs.map(async doc => {
          let docStatus = {};
          try {
            docStatus = (await signatureStatus(doc.documentDescription, doc.documentID)) || {};
          } catch (e) {
            docStatus = {};
          }
          // createDate arrives as a UTC timestamp from the server. Parse it as UTC
          // and convert to the viewer's local time zone so the Requested Time column
          // reflects the user's wall clock (e.g. PST) rather than raw UTC.
          const formatDate = dateStr => moment.utc(dateStr).local().format('MM/DD/YYYY - hh:mm A');
          signdocMap.set(doc.documentName, doc.documentID);
          return {
            displayName: doc.documentDisplayName || doc.documentName,
            documentID: doc.documentID,
            status: docStatus || {},
            time: formatDate(doc.createDate),
          };
        }),
      );

      setState({ signatureDocMap: signdocMap, otherDocStatuses });
    }
  };

  /**
   * Checks which signatures we have stored in the DB for the ERO,
   * Preparer or the Amended Preparer.
   *
   * @function handleCheckDBSignaturesExist
   */
  const handleCheckDBSignaturesExist = async () => {
    const dbSignaturesExist = await checkDBSignaturesExist(
      preparerID,
      amendedPreparerID,
      returnID,
      isSelfPreparing,
    );

    const signaturesCollected = [];

    // Setting to false matches the signatures needed redux state for skipping prep signature
    signaturesCollected[SIGNEE.PREPARER] = false;

    if (amendedPreparerID) {
      if (doWeHaveSignatures(dbSignaturesExist, 2)) {
        signaturesCollected[SIGNEE.AMENDED_PREPARER] = true;
      }
    }

    docsNeedingSigned(signaturesCollected);
  };

  /**
   * Sets the documents that are needing signed.
   *
   * @param {Object[]} sigsCollected The signatures that we already have in the DB.
   */
  const docsNeedingSigned = sigsCollected => {
    const docsNeedingSigned = remainingSignaturesNeeded(signaturesNeeded, sigsCollected);
    setState({ docsNeedingSigned });
  };

  // The set of signees we surface in the signature listing.
  const DISPLAYABLE_SIGNEES = new Set([
    SIGNEE.TAXPAYER,
    SIGNEE.TAXPAYER_SECOND,
    SIGNEE.SPOUSE,
    SIGNEE.SPOUSE_SECOND,
    SIGNEE.AMENDED_PREPARER,
    SIGNEE.OFFICER,
  ]);

  // Display labels for each signee, matching desktop's "Remote Signature Listing".
  const SIGNEE_DISPLAY_NAME = {
    [SIGNEE.TAXPAYER]: 'Taxpayer',
    [SIGNEE.SPOUSE]: 'Spouse',
    [SIGNEE.TAXPAYER_SECOND]: 'Taxpayer II',
    [SIGNEE.SPOUSE_SECOND]: 'Spouse II',
    [SIGNEE.OFFICER]: 'Officer',
    [SIGNEE.AMENDED_PREPARER]: 'Amended Preparer',
  };

  const SIGNEE_TO_CATEGORY = {
    [SIGNEE.TAXPAYER]: 1,
    [SIGNEE.SPOUSE]: 2,
    [SIGNEE.TAXPAYER_SECOND]: 5,
    [SIGNEE.SPOUSE_SECOND]: 6,
    [SIGNEE.OFFICER]: 7,
    [SIGNEE.AMENDED_PREPARER]: 9,
  };

  /**
   * Builds a single aggregated listing row for one document, collapsing all of
   * its signees into one entry. Mirrors desktop's "Remote Signature Listing":
   * a "Waiting: Taxpayer, Spouse" status while signatures are pending, or a
   * "Completed/Archived {date}" status once everyone has signed.
   *
   * @param {Object} args
   * @param {string} args.document The document key (e.g. Final Tax Return).
   * @param {string} args.displayLabel The label shown in the DOCUMENT column.
   * @param {string} args.time The requested/archive time string.
   * @param {number} [args.docID] The archive document ID (other doc types).
   * @param {Object} args.statusObj The per-signee status object for the document.
   * @param {boolean} [args.allowAmendedPreparer] Whether to include the amended
   *   preparer signee (only relevant for the amended return).
   * @return {Object|null} The aggregated row, or null when no signees apply.
   */
  const buildDocumentRow = ({
    document,
    displayLabel,
    time,
    docID,
    statusObj,
    allowAmendedPreparer = false,
  }) => {
    const signeeKeys = Object.keys(statusObj || {}).filter(key => {
      if (!DISPLAYABLE_SIGNEES.has(key)) return false;
      if (key === SIGNEE.AMENDED_PREPARER && !allowAmendedPreparer) return false;
      return true;
    });

    if (signeeKeys.length === 0) return null;

    const pendingSignees = signeeKeys.filter(
      key => get(statusObj[key], 'signatureStatus') === false,
    );
    const allSigned = pendingSignees.length === 0;

    let statusText;
    if (allSigned) {
      const signedDates = signeeKeys
        .map(key => get(statusObj[key], 'updateDate'))
        .filter(Boolean)
        // updateDate is a UTC timestamp; parse as UTC so the latest-signed date is
        // picked correctly and rendered in the viewer's local time zone.
        .map(date => moment.utc(date))
        .filter(m => m.isValid());
      const latest = signedDates.length ? moment.max(signedDates).local().format('MM/DD/YYYY') : '';
      statusText = `Completed/Archived ${latest}`.trim();
    } else {
      statusText = `Waiting: ${pendingSignees
        .map(key => SIGNEE_DISPLAY_NAME[key] || key)
        .join(', ')}`;
    }

    return {
      document,
      displayLabel,
      time,
      docID,
      statusText,
      allSigned,
      pendingCategoryIDs: pendingSignees.map(key => SIGNEE_TO_CATEGORY[key]),
    };
  };

  /**
   * Builds the signature listing, one row per document. Each row aggregates the
   * statuses of every signee on that document, matching desktop's layout.
   */
  const documentsNeedingSigned = () => {
    const rows = [];

    const federalFormList = formList.find(list => list?.desc?.trim() === 'FEDERAL');
    const isAmendedReturnAttached = doesValueExistInAnArrayOfObjects(
      federalFormList?.Children || [],
      'USEX',
    );

    const finalRow = buildDocumentRow({
      document: FINAL_RETURN,
      displayLabel: state.taxReturnDisplayName,
      time: state.taxReturnTime,
      statusObj: state.taxReturnStatus,
    });
    if (finalRow) rows.push(finalRow);

    if (Object.keys(state.fnbaStatus).length > 0) {
      const fnbaRow = buildDocumentRow({
        document: FNBA,
        displayLabel: state.fnbaDisplayName,
        time: state.fnbaTime,
        statusObj: state.fnbaStatus,
      });
      if (fnbaRow) rows.push(fnbaRow);
    }

    if (isAmendedReturnAttached) {
      const amendedRow = buildDocumentRow({
        document: AMENDED_RETURN,
        displayLabel: state.amendedReturnDisplayName,
        time: state.amendedReturnTime,
        statusObj: state.amendedReturnStatus,
        allowAmendedPreparer: true,
      });
      if (amendedRow) rows.push(amendedRow);
    }

    // Add rows for any other remote signature document types (e.g. Print Current Form).
    // Desktop shows signatures for all archived/signed document types, not just Final/FNBA/Amended.
    state.otherDocStatuses.forEach(otherDoc => {
      const otherRow = buildDocumentRow({
        document: otherDoc.displayName,
        displayLabel: otherDoc.displayName,
        docID: otherDoc.documentID,
        time: otherDoc.time,
        statusObj: otherDoc.status,
      });
      if (otherRow) rows.push(otherRow);
    });

    setState({ signaturesReq: rows });
  };

  // Resends the remote signature request to every pending signee on a document.
  const handleResendAll = async (archiveDocID, categoryIDs) => {
    setState({ isResending: true });
    try {
      for (const signatureCategoryID of categoryIDs) {
        await XlinkAPI.resendRemoteSignature(returnID, archiveDocID, signatureCategoryID);
      }
      dispatch(formViewerActions.remoteSignSuccessfulSet(true));
    } catch (error) {
      ErrorHelpers.handleError('Error resending signature request', error);
    } finally {
      setState({ isResending: false });
    }
  };

  // Deletes a pending remote signature request for a document. Mirrors desktop: the request
  // is removed entirely (any already-collected signatures are voided), but the archived PDF
  // remains in the Document Archive. Completed requests are blocked server-side and disabled
  // in the UI.
  const handleDeleteSignature = async archiveDocID => {
    if (Number.isNaN(archiveDocID)) return;
    setState({ isDeleting: true });
    try {
      await XlinkAPI.deleteRemoteSignature(returnID, archiveDocID);
      await handleTimeAndStatus();
      await handleCheckDBSignaturesExist();
    } catch (error) {
      ErrorHelpers.handleError('Error deleting signature request', error);
    } finally {
      setState({ isDeleting: false });
    }
  };

  // Opens the per-row context (hamburger) menu, anchored to the clicked button.
  const handleContextMenuToggle = (event, selected) => {
    setState({ contextMenuAnchor: event.currentTarget, contextMenuSelected: selected });
  };

  // Closes the context menu without taking an action.
  const handleContextMenuClose = () => {
    setState({ contextMenuAnchor: null, contextMenuSelected: null });
  };

  // These indicators are only used to improve info messages
  const hasBankApp = state.printFinalBankDoc && state.bankID > 0;

  // determine what single or combo msg to display
  const secondarySigneeTooltipMsg =
    hasBankApp && state.is4547FormAttached ? (
      <>
        {BANK_APP_INFO_MSG}
        <br />
        <br />
        {FORM_4547_INFO_MSG}
      </>
    ) : hasBankApp ? (
      BANK_APP_INFO_MSG
    ) : state.is4547FormAttached ? (
      FORM_4547_INFO_MSG
    ) : (
      ''
    );

  return (
    <>
      {state.isResending && (
        <Spinner
          size={100}
          color="blue"
          loadingText="Sending Request..."
          textColor="white"
          bgColor="grey"
          lockActions={true}
          skipDelay
        />
      )}
      {state.isDeleting && (
        <Spinner
          size={100}
          color="blue"
          loadingText="Deleting Request..."
          textColor="white"
          bgColor="grey"
          lockActions={true}
          skipDelay
        />
      )}
      <Table>
        <TableHead>
          <TableRow>
            {[...SIGNATURE_LIST_TABLE_COLUMNS, 'ACTION'].map(column => {
              return (
                <TableCell key={column} classes={{ root: classes.cellHeader }}>
                  {column}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {state.signaturesReq.map((row, i) => {
            const archiveDocID = parseInt(row.docID ?? state.signatureDocMap.get(row.document), 10);
            const menuID = 'sig-menu-' + i;
            return (
              <Fragment key={'fragment-' + i}>
                <TableRow key={'row-' + i} hover>
                  <TableCell
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    {row.displayLabel || row.document}
                  </TableCell>
                  <TableCell
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    {row.time}
                  </TableCell>
                  <TableCell
                    variant="body"
                    classes={{ root: classes.cellRootStatus }}
                    component="th"
                    scope="row"
                  >
                    <div className={classes.statusCellContent}>
                      <Chip
                        icon={
                          row.allSigned ? (
                            <CheckCircleIcon classes={{ root: classes.completedChipIcon }} />
                          ) : (
                            <WarningIcon classes={{ root: classes.pendingChipIcon }} />
                          )
                        }
                        label={row.statusText}
                        classes={{
                          root: row.allSigned ? classes.completedChip : classes.pendingChip,
                        }}
                      />
                      {!row.allSigned && (hasBankApp || state.is4547FormAttached) ? (
                        <Tooltip title={secondarySigneeTooltipMsg}>
                          <InfoIcon fontSize="small" classes={{ root: classes.cellTooltipIcon }} />
                        </Tooltip>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    <div className={classes.actionCellContent}>
                      {/* The skewer is always visible (matching the Document Archive Action
                          column) but disables itself when the row has no available actions
                          (completed requests can be neither resent nor deleted). The hover
                          tooltip on the disabled button explains why instead of opening a
                          menu of grayed-out items. */}
                      <div className={classes.actionMenuWrapper}>
                        <Tooltip
                          title={
                            row.allSigned || Number.isNaN(archiveDocID)
                              ? 'No actions available — all signatures for this request have been collected'
                              : ''
                          }
                        >
                          <span>
                            <Button
                              className={`contextMenuButton ${classes.contextMenuButton}`}
                              aria-haspopup="true"
                              aria-owns={state.contextMenuSelected === menuID ? menuID : null}
                              disabled={row.allSigned || Number.isNaN(archiveDocID)}
                              onClick={e => handleContextMenuToggle(e, menuID)}
                            >
                              <img src={menuVertical} className={classes.contextMenuIcon} />
                            </Button>
                          </span>
                        </Tooltip>
                        <Menu
                          id={menuID}
                          anchorEl={state.contextMenuAnchor}
                          open={Boolean(state.contextMenuSelected === menuID)}
                          onClose={handleContextMenuClose}
                        >
                          <MenuItem
                            disabled={row.allSigned || Number.isNaN(archiveDocID)}
                            onClick={() => {
                              handleContextMenuClose();
                              handleResendAll(archiveDocID, row.pendingCategoryIDs);
                            }}
                          >
                            Resend
                          </MenuItem>
                          <MenuItem
                            disabled={row.allSigned || Number.isNaN(archiveDocID)}
                            onClick={() => {
                              handleContextMenuClose();
                              setState({
                                isConfirmDeleteModalOpen: true,
                                deleteArchiveDocID: archiveDocID,
                              });
                            }}
                          >
                            Delete
                          </MenuItem>
                        </Menu>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {state.isConfirmDeleteModalOpen && (
        <SimpleDialog
          open={state.isConfirmDeleteModalOpen}
          onClose={() => setState({ isConfirmDeleteModalOpen: false })}
          onConfirm={() => {
            setState({ isConfirmDeleteModalOpen: false });
            handleDeleteSignature(state.deleteArchiveDocID);
          }}
          dialogTitle="WARNING"
          contentText="Deleting this remote signature request will void any signatures already collected and cannot be undone. Are you sure you want to delete it?"
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </>
  );
};

export default withStyles(styles)(SignatureList);
